import { makeAutoObservable } from "mobx";
import firebase from "firebase/compat/app";
import workspaces from "./workspaces";
import {
  BoardItemCard,
  Booking,
  Ticket,
  TicketStatus,
  TicketType,
  typeTypeColorMap,
} from "../models";
import { parseFirebaseInFilterValue } from "../utils/parseFirebaseFilterValue";
import axios from "axios";
import boards from "./boards";

export type CalendarEvent = {
  id: string;
  title: string;
  type: "ticket" | "event";
  start: string;
  end?: string;
  allDay?: boolean;
  resource?: any;
  meta?: any;
};

class CalendarStore {
  private db: firebase.firestore.Firestore;
  events: CalendarEvent[] = [];

  constructor() {
    makeAutoObservable(this);
    this.db = firebase.firestore();
  }

  loadEvents = async (dates: string[], group?: number) => {
    if (!workspaces.selectedWorkspace) return;

    this.setEvents([]);
    const bookings = await this.getBookings(group);
    const tickets = await this.getTickets(dates, group);
    const events = [...bookings, ...tickets];
    this.setEvents(events);
  };

  getBookings = async (group?: number) => {
    if (!workspaces.selectedWorkspace) return [];

    const bookingSnapshot = await this.db
      .collection("workspaces")
      .doc(workspaces.selectedWorkspace.id)
      .collection("bookings")
      .where(
        "event.livecardId",
        "in",
        group
          ? [group]
          : parseFirebaseInFilterValue(
              workspaces.activeLiveChatCards.map((c) => c.id)
            )
      )
      .get();

    return bookingSnapshot.docs
      .map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Booking)
      )
      .filter((f) => !f.cancelledAt)
      .map((booking) => ({
        id: booking.id,
        title: booking.user?.name ?? booking.id,
        type: "event",
        start: booking.start as any,
        end: booking.end as any,
        color: booking.event.color,
        meta: booking,
      })) as CalendarEvent[];
  };

  getTickets = async (
    dates: string[],
    group?: number,
    includeOverdue?: boolean
  ) => {
    if (!workspaces.selectedWorkspace) return [];

    const selectedFunction = workspaces.selectedWorkspace?.functions?.find(
      (f) => f.slug === "op"
    );
    if (selectedFunction && selectedFunction?.meta?.bot?.id) {
      let page = 1;
      let allData: any[] = [];
      let hasMore = true;

      while (hasMore) {
        const {
          data: { response: data, has_more },
        } = await axios.get(
          `${process.env.REACT_APP_CHATHUB_APP_URL}/api/${selectedFunction.meta.bot.id}/board/all-tickets`,
          {
            params: {
              key: selectedFunction.meta.bot.key,
              password: selectedFunction.meta.bot.password,
              livecard_id: group,
              start_date: dates[0],
              end_date: dates[1],
              include_overdue: includeOverdue ? 1 : 0,
              paginate: 1,
              page,
            },
          }
        );

        allData = allData.concat(data);
        hasMore = has_more;
        page++;
      }

      const tickets = allData.map(boards.boardItemCardAdapter) as BoardItemCard[];

      return tickets.map(
        (ticket) =>
          ({
            id: ticket.uuid,
            type: "ticket",
            title: ticket.name,
            start: ticket.dueDate?.toISO(),
            allDay: true,
            color: typeTypeColorMap[(ticket.type as TicketType) ?? "task"],
            meta: ticket,
          } as CalendarEvent)
      );
    }
    return [];
  };

  markTicketAsDone = async (ticket: Ticket) => {
    this.events = this.events.map((event) => {
      if (event.id === ticket.id) {
        return {
          ...event,
          meta: {
            ...event.meta,
            status: TicketStatus.DONE,
          },
        };
      }
      return event;
    });
  };

  updateBooking = (booking: Booking) => {
    this.setEvents(
      this.events
        .map((f) => {
          if (f.id === booking.id && f.type === "event") {
            return {
              ...f,
              title: booking.user?.name ?? booking.id,
              start: booking.start as any,
              meta: booking,
            };
          }
          return f;
        })
        .filter((f) => {
          if (
            f.id === booking.id &&
            f.type === "event" &&
            f.meta?.cancelledAt
          ) {
            return false;
          }
          return true;
        })
    );
  };

  private setEvents = (events: CalendarEvent[]) => {
    this.events = events;
  };
}

export default new CalendarStore();
